import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../Toast';
import { isAdminUser, isAdmin } from '../../utils/admin';
import { logSignupEvent } from '../../utils/signupTelemetry';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  message?: string;
  title?: string;
  initialMode?: 'signin' | 'signup';
  onLoginSuccess?: () => void;
  required?: boolean;
  intent?: string; // e.g. 'affiliate' — passed through auth so dashboard can act on it
  // Google OAuth only. The admin gate uses this: the owner signs in with the
  // Google account the admin allowlist is keyed on, so an email/password form
  // and a "Sign up" link on /admin are dead options on a restricted route.
  googleOnly?: boolean;
}

export default function AuthModal({ isOpen, onClose, message, title, initialMode = 'signin', onLoginSuccess, required = false, intent, googleOnly = false }: AuthModalProps) {
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [justSignedIn, setJustSignedIn] = useState(false);
  const [justSignedUp, setJustSignedUp] = useState(false);

  const { signUp, signIn, signInWithGoogle, user } = useAuth();
  const { success, error: showError } = useToast();
  const navigate = useNavigate();

  // After email/password auth — redirect with intent if present, otherwise normal flow
  useEffect(() => {
    if (justSignedUp && user) {
      setJustSignedUp(false);
      if (intent) {
        setJustSignedIn(false);
        navigate(`/dashboard?join=${intent}`);
        onClose();
      } else {
        handleRedirect();
      }
    } else if (justSignedIn && user && !justSignedUp) {
      setJustSignedIn(false);
      if (intent) {
        navigate(`/dashboard?join=${intent}`);
        onClose();
      } else {
        handleRedirect();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, justSignedIn, justSignedUp]);

  const handleRedirect = async () => {
    if (!user) {
      navigate('/');
      return;
    }

    const returnUrl = localStorage.getItem('auth_return_url');
    const protectedPaths = ['/dashboard', '/profile', '/settings'];
    const currentPath = window.location.pathname;

    if (returnUrl && returnUrl !== '/' && !returnUrl.startsWith('/auth')) {
      localStorage.removeItem('auth_return_url');
      navigate(returnUrl);
      return;
    }

    if (protectedPaths.some(p => currentPath.startsWith(p))) {
      return;
    }

    try {
      let adminStatus = false;
      try {
        adminStatus = await isAdmin();
      } catch {
        adminStatus = isAdminUser(user);
      }
      navigate(adminStatus || isAdminUser(user) ? '/admin' : '/dashboard');
    } catch {
      navigate(isAdminUser(user) ? '/admin' : '/dashboard');
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentPath = window.location.pathname;
    if (currentPath !== '/' && !currentPath.startsWith('/auth')) {
      localStorage.setItem('auth_return_url', currentPath + window.location.search);
    }
    setError(null);
    setLoading(true);
    try {
      if (isSignUp) {
        const { error } = await signUp(email, password);
        if (error) {
          setError(error.message);
          showError(error.message);
          logSignupEvent({ stage: 'email_signup', success: false, method: 'email', email, intent, error_message: error.message });
        } else {
          logSignupEvent({ stage: 'email_signup', success: true, method: 'email', email, intent });
          success('Account created successfully!');
          setEmail('');
          setPassword('');
          setJustSignedUp(true);
          setJustSignedIn(true);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message);
          showError(error.message);
        } else {
          success('Signed in successfully!');
          if (onLoginSuccess) {
            onLoginSuccess();
            onClose();
          } else {
            onClose();
            setEmail('');
            setPassword('');
            setJustSignedIn(true);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    localStorage.setItem('auth_return_url', window.location.pathname + window.location.search);
    setError(null);
    setLoading(true);
    // If there's an intent, pass it in the redirect URL so AuthCallback can forward it
    const redirectUrl = intent
      ? `${window.location.origin}/auth/callback?intent=${intent}`
      : undefined;
    const { error } = await signInWithGoogle(redirectUrl);
    if (error) {
      setError(error.message);
      setLoading(false);
      logSignupEvent({ stage: 'google_oauth_start', success: false, method: 'google', intent, error_message: error.message });
    }
    // OAuth redirects to AuthCallback which handles the rest
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 backdrop-blur-sm"
        onClick={required ? undefined : onClose}
      >
        <div
          className="bg-black/50 rounded-none p-6 w-full max-w-md mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">
              {title || (isSignUp ? 'Sign Up' : 'Sign In')}
            </h2>
            {!required && (
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white transition"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>

          {message && (
            <div className="mb-6 px-4 py-3 bg-white/5 text-white text-sm leading-relaxed">
              {message}
            </div>
          )}

          {!googleOnly && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 bg-white/5 text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-4 py-2 bg-white/5 text-white placeholder-white/40 focus:outline-none focus:bg-white/10 transition-colors"
                placeholder="••••••"
              />
            </div>

            {error && (
              <div className="px-4 py-2 bg-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-white text-black font-semibold rounded-none hover:bg-white/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : isSignUp ? 'Sign Up' : 'Sign In'}
            </button>
          </form>
          )}

          {googleOnly && error && (
            <div className="px-4 py-2 bg-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className={googleOnly ? '' : 'mt-6'}>
            {!googleOnly && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/20"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-black text-white/60">OR</span>
              </div>
            </div>
            )}

            <button
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="mt-4 w-full px-4 py-2 bg-white/5 text-white font-medium hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </button>
          </div>

          {!googleOnly && (
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-white/60 hover:text-white text-sm transition"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
          )}
        </div>
      </div>
    </>
  );
}
